import React, { useEffect, useRef, useState } from 'react'
import { Table, Button, ButtonGroup } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'

import { useLanguage } from '../../contexts/LanguageContext'
import { POI_PATH } from '../../config/routes'
import {
  slugify,
  getPoiLabel,
  getAltitude,
  getPoiColor,
  getPoiMarkerPath,
  getGpsxMax,
  getGpsxMin,
  getGpsyMax,
  getGpsyMin,
  calculateZoom,
} from '../../utils/utils'
import config from '../../config/config'
import DisplayPois from '../PointsOfInterest/DisplayPois'

const AccomodationList = ({ pois }) => {
  const { t } = useTranslation()
  const { activeLanguage } = useLanguage()
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const mapInstanceRef = useRef(null)
  const infoWindowRef = useRef(null)
  const selectedPolylineRef = useRef(null)

  const getStoredHiddenPois = () => {
    const stored = localStorage.getItem('hiddenPois')
    return stored ? JSON.parse(stored) : []
  }

  const [displayedPois, setDisplayedPois] = useState(() => {
    const hidden = getStoredHiddenPois()
    return pois
      .filter((p) => p.status !== 'DRAFT')
      .map((p) => ({ ...p, isDisplayed: !hidden.includes(p.id) }))
  })

  // --- Chargement et affichage de la carte et des marqueurs ---
  useEffect(() => {
    if (!pois.length) return

    const loadScript = () => {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      script.async = true
      script.defer = true
      script.onload = initMap
      document.head.appendChild(script)
    }

    const initMap = () => {
      if (!window.google) return

      const maxX = getGpsxMax(pois, false) + 0.01
      const minX = getGpsxMin(pois, false)
      const maxY = getGpsyMax(pois, false)
      const minY = getGpsyMin(pois, false)

      const map = new window.google.maps.Map(mapRef.current, {
        zoom:
          maxX && minX && maxY && minY
            ? calculateZoom(maxX, minX, maxY, minY)
            : config.mapPoiZoom,
        center: { lat: (maxX + minX) / 2, lng: (maxY + minY) / 2 },
        mapTypeId: 'terrain',
        styles: [
          {
            featureType: 'all',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }],
          },
          {
            featureType: 'landscape.natural',
            elementType: 'geometry',
            stylers: [{ visibility: 'simplified' }, { color: '#7FBF7F' }],
          },
        ],
      })
      mapInstanceRef.current = map
      const bounds = new window.google.maps.LatLngBounds()

      displayedPois.forEach((poi) => addMarker(poi, map, bounds))

      infoWindowRef.current = new window.google.maps.InfoWindow({
        disableAutoPan: false,
        boxClass: 'infoWindowBox',
      })
      infoWindowRef.current.addListener('closeclick', resetPolylineOpacity)
    }

    const addMarker = (poi, map, bounds) => {
      const marker = new window.google.maps.Marker({
        position: { lat: poi.gpsx, lng: poi.gpsy },
        map: poi.isDisplayed ? map : null,
        title: poi.name,
        icon: getPoiMarkerPath(poi),
      })
      bounds.extend(marker.getPosition())
      markersRef.current.push({ poiId: poi.id, marker })

      marker.addListener('click', () => {
        const path = POI_PATH.replace(':slug', slugify(poi.labelFr)).replace(
          ':id',
          poi.id,
        )
        infoWindowRef.current.setContent(`
          <div>
            <h4>${getPoiLabel(poi, activeLanguage)}</h4>
            <div style="text-align:center">
              <button class="btn btn-primary" onclick="window.location.href='${path}'">
                ${t('SHOW')}
              </button>
              <button class="btn btn-secondary" onclick="document.getElementById('hide-poi-${
                poi.id
              }').click()">
                ${t('HIDE')}
              </button>
            </div>
          </div>
        `)
        infoWindowRef.current.open(map, marker)
        updatePolylineOpacity(poi.id)
      })
    }

    if (!window.google) loadScript()
    else initMap()
  }, []) // ← on ne repose qu’une fois

  // Met à jour les marqueurs si on cache/affiche des POIs
  useEffect(() => {
    markersRef.current.forEach(({ poiId, marker, polyline }) => {
      const poi = displayedPois.find((p) => p.id === poiId)
      if (marker) marker.setMap(poi.isDisplayed ? mapInstanceRef.current : null)
      if (polyline)
        polyline.setOptions({ strokeOpacity: poi.isDisplayed ? 1 : 0 })
    })
  }, [displayedPois])

  // --- Fonctions de contrôle d’affichage ---
  const handleToggleDisplay = (poiId, shouldDisplay) => {
    const updated = displayedPois.map((p) =>
      p.id === poiId ? { ...p, isDisplayed: shouldDisplay } : p,
    )
    setDisplayedPois(updated)
    localStorage.setItem(
      'hiddenPois',
      JSON.stringify(updated.filter((p) => !p.isDisplayed).map((p) => p.id)),
    )
  }

  const updatePolylineOpacity = (selectedId) => {
    markersRef.current.forEach(({ poiId, polyline }) => {
      if (!polyline) return
      polyline.setOptions({ strokeOpacity: poiId === selectedId ? 1 : 0.5 })
      if (poiId === selectedId) selectedPolylineRef.current = polyline
    })
  }

  const resetPolylineOpacity = () => {
    markersRef.current.forEach(({ polyline }) => {
      if (polyline) polyline.setOptions({ strokeOpacity: 1 })
    })
    selectedPolylineRef.current = null
  }

  return (
    <div className="poi-list-wrapper mt-5">
      <Table bordered className="rdb-table">
        <thead>
          <tr>
            <th>{t('N°')}</th>
            <th>{t('CITY')}</th>
            <th>{t('LABEL')}</th>
            <th>{t('ALTITUDE')}</th>
            <th>{t('DISPLAYED')} ?</th>
          </tr>
        </thead>
        <tbody>
          {displayedPois.map((poi) => {
            const bg = getPoiColor(poi)
            return (
              <tr key={poi.id}>
                <td className="text-center">
                  <Link
                    to={POI_PATH.replace(':slug', slugify(poi.labelFr)).replace(
                      ':id',
                      poi.id,
                    )}
                    className="poi-list-numero-link btn"
                    style={{ '--background-color': bg }}
                  >
                    {poi.numero}
                  </Link>
                </td>
                <td>{poi.city.label}</td>
                <td>{getPoiLabel(poi, activeLanguage)}</td>
                <td className="text-center">{getAltitude(poi)}m</td>
                <td className="text-center">
                  <ButtonGroup className="mb-2">
                    <Button
                      variant={poi.isDisplayed ? 'primary' : 'secondary'}
                      onClick={() => handleToggleDisplay(poi.id, true)}
                      className={`btn-sm poi-list-btn-sm-custom ${
                        poi.isDisplayed ? 'activated-noclic' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      id={`hide-poi-${poi.id}`}
                      variant={!poi.isDisplayed ? 'primary' : 'secondary'}
                      onClick={() => handleToggleDisplay(poi.id, false)}
                      className={`btn-sm poi-list-btn-sm-custom ${
                        !poi.isDisplayed ? 'activated-noclic' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={faEyeSlash} />
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      <div id="map" ref={mapRef} style={{ height: '500px', width: '100%' }} />

      <hr />
      <DisplayPois pois={pois} isCity={true} isCategory={false} />
    </div>
  )
}

export default AccomodationList
